// The most perfect location pin icon you ever saw.
//  Coded at 2am like all good things.
//
import { LocationOnTwoTone } from '@mui/icons-material';
import { Avatar, Badge, Box, IconButton, Popover, Typography } from '@mui/material';
import { memo } from 'react';

import { AXLE_TEAL, TEXT_GRAY } from '../../../../constants/colors';
import { QhpWithLocationInfo } from '../../../../fetching/api/liveLocations';
import { useStateWithDerivedBoolean } from '../../../../utils/booleanState';
import { ClickHandler } from '../../../../utils/clickEvent';
import { formatInitials } from '../../../../utils/formatting';
import { NewTabLink } from '../../../../utils/links';
import { Component, ReturnComponent } from '../../../../utils/reactTypes';
import { If } from '../../../utils/conditionalComponents';

type CustomPinIcon = ({ onClick }: { onClick?: ClickHandler }) => ReturnComponent;

const LocationPinIcon = ({
    color,
    badgeContent = undefined,
    onClick = undefined,
}: {
    color: string;
    badgeContent?: Component | undefined;
    onClick?: ClickHandler;
}) => {
    return (
        <IconButton
            onClick={onClick}
            sx={{
                // PERFECTLY place the icon on top of the route.
                transform: 'translate(-50%, -75%)',

                // Now let's give these icons some CHONK.
                //  Yes, we're going to directly style the SVG drawing inside the <LocationOnTwoTone/> icon.
                //  Otherwise, the pins kinda blend into each other and with the route.

                // Outer outline.
                '& svg path:nth-of-type(2)': {
                    fill: 'black',
                },
                // Inner circle.
                '& svg circle': {
                    fill: 'black',
                },
                // Fill in the middle.
                '& svg path:nth-of-type(1)': {
                    // I initially tried using the `color` CSS property, but while it works in
                    //  Chrome, it doesn’t work in Safari.
                    fill: color,
                    opacity: 0.9, // The default opacity is 0.3, which is too low.
                },
            }}
        >
            <Badge
                // If `badgeContent` is falsy, the badge will not show.
                badgeContent={badgeContent}
                sx={{
                    '& .MuiBadge-badge': {
                        // Reset the default styles.
                        padding: 0,
                        minWidth: 'unset',
                        minHeight: 'unset',
                        // Configure our own size.
                        height: '18px',
                        width: '18px',
                        fontSize: '14px !important',
                        // Positioning it correctly.
                        transform: 'translate(0px, -7.5px)',
                        // Coloring it correctly.
                        color: 'white',
                        backgroundColor: '#333',
                    },
                }}
            >
                <LocationOnTwoTone
                    style={{
                        // Ahhhhh yes, gimme that chonk.
                        fontSize: '35px',
                    }}
                />
            </Badge>
        </IconButton>
    );
};

// We memoize this component, as it was being rendered 5 times per page load,
//  Multiply that by 100, for one pin each for 100 visits, and it slowed down
//  the page render a lot.
export const GoogleMapPin = memo(
    ({
        // We don’t use these props ourselves. But react-google-maps "inspects"
        //  the props of the child components and uses them to position the markers.
        //  Is that weird? Yeah.
        // eslint-disable-next-line unused-imports/no-unused-vars
        lat,
        // eslint-disable-next-line unused-imports/no-unused-vars
        lng,
        // We actually use these props, yay!
        color,
        badgeContent = undefined,
        popoverComponent = null,
        onClick = undefined,
        CustomPinIcon = undefined,
    }: {
        lat: string;
        lng: string;
        color: string;
        badgeContent?: Component | undefined;
        popoverComponent?: Component | undefined;
        onClick?: ClickHandler;
        CustomPinIcon?: CustomPinIcon;
    }) => {
        //

        // Popover state.
        const hasPopover = !!popoverComponent;
        const [popoverAnchor, setPopoverAnchor, isPopoverOpen, closePopover] =
            useStateWithDerivedBoolean<HTMLElement>(null);

        // Click event.
        const isClickable = !!onClick || hasPopover;
        const onClickCombined: ClickHandler = (event) => {
            if (onClick) onClick(event);
            if (hasPopover) setPopoverAnchor(event.currentTarget);
        };
        const onClickFinal = isClickable ? onClickCombined : undefined;

        return (
            <>
                {!CustomPinIcon ? (
                    <LocationPinIcon
                        color={color}
                        badgeContent={badgeContent}
                        onClick={onClickFinal}
                    />
                ) : (
                    <CustomPinIcon onClick={onClickFinal} />
                )}

                <If condition={hasPopover}>
                    <Popover
                        open={isPopoverOpen}
                        onClose={closePopover}
                        anchorEl={popoverAnchor}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        <Box padding={2}>{popoverComponent}</Box>
                    </Popover>
                </If>
            </>
        );
    },
);

GoogleMapPin.displayName = 'GoogleMapPin';

export const getBadgeContentFromStopIndex = (stopIndex: number | null): Component | undefined =>
    stopIndex === null ? undefined : <>{stopIndex + 1}</>;

export const iconForQhpWithLocationInfo = (
    qhpWithLocationInfo: QhpWithLocationInfo,
): CustomPinIcon => {
    const component = ({ onClick }: { onClick?: ClickHandler }) => (
        <Box
            sx={{
                // Control the size.
                height: '25px',
                width: '25px',

                // PERFECTLY place the icon on top of the route.
                transform: 'translate(-50%, -75%)',

                // Help position the pulsing animation.
                position: 'relative',
            }}
        >
            {/* Pulsing rings for the "live location" effect. */}
            {/* This needs to come before the <Avatar/> component, so it goes under it. */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '25px',
                    height: '25px',
                    borderRadius: '50%',
                    border: `2px solid ${AXLE_TEAL}`,
                    animation: 'pulse 3s infinite',
                    '@keyframes pulse': {
                        '0%': {
                            width: '25px',
                            height: '25px',
                            opacity: 0.8,
                        },
                        '50%': {
                            width: '40px',
                            height: '40px',
                            opacity: 0,
                        },
                        '100%': {
                            width: '25px',
                            height: '25px',
                            opacity: 0,
                        },
                    },
                }}
            />
            <Avatar
                onClick={onClick}
                src={qhpWithLocationInfo.qhp.logo_url ?? undefined}
                sx={{
                    // Need to repeat the size yet again here.
                    height: '25px',
                    width: '25px',

                    // Text formatting (needs to be specified here).
                    fontSize: '1em',
                    bgcolor: TEXT_GRAY,

                    // Make it look clickable.
                    cursor: 'pointer',
                }}
            >
                {formatInitials(
                    qhpWithLocationInfo.qhp.first_name,
                    qhpWithLocationInfo.qhp.last_name,
                )}
            </Avatar>
        </Box>
    );
    component.displayName = 'IconForQhpWithLocationInfo';
    return component;
};

export const popoverForQhpWithLocationInfo = (
    qhpWithLocationInfo: QhpWithLocationInfo,
): ReturnComponent => {
    return (
        <Typography>
            <NewTabLink to={`/clinicians/${qhpWithLocationInfo.qhp.uuid}`}>
                {qhpWithLocationInfo.qhp.name}
            </NewTabLink>{' '}
            last updated their location at {qhpWithLocationInfo.location_info.timestamp.fromNow()}.
        </Typography>
    );
};
